import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthProvider } from '@domain/authentication';
import { AccountModel, UserService } from '@domain/registration';
import { NavController } from '@ionic/angular';
import { AppRoutingKeys, ErrorCodes, ToastDuration, ToastService } from '@shared';
import { TranslateService } from '@ngx-translate/core';
import { AccountBaseComponent } from '../base';
import { Router } from '@angular/router';
import { AccountStateService } from '../../services/account-state.service';
import { firstValueFrom } from 'rxjs';
import { WorkspaceModel, WorkspaceRepository } from '@database/central';

@Component({
	selector: 'app-registraion-pin',
	templateUrl: './account-pin.component.html',
	styleUrls: ['../../account.scss']
})
export class AccountPinComponent extends AccountBaseComponent implements OnInit {
	public constructor(
		private accountStateService: AccountStateService,
		private toastService: ToastService,
		private translateService: TranslateService,
		private userService: UserService,
		private authProvider: AuthProvider,
		private workspaceRepository: WorkspaceRepository,
		public navController: NavController,
		public router: Router
	) {
		super(navController, router);
	}

	public get helperText(): string {
		const content = this.isRegistration() ? 'registration.content.pin' : 'reset.content.pin';
		return this.translateService.instant(content);
	}

	public ngOnInit(): void {
		this.fieldFormControl = new FormControl('', [
			Validators.required,
			Validators.maxLength(4),
			Validators.minLength(4),
			Validators.pattern('^[0-9]*$')
		]);

		this.registrationForm = new FormGroup({
			fieldFormControl: this.fieldFormControl
		});
	}

	public async onSubmit(formValues: { fieldFormControl: string }): Promise<void> {
		if (!this.isFormValid()) {
			return;
		}
		const messageSource = this.isRegistration()
			? AppRoutingKeys.urlPathRegistration
			: AppRoutingKeys.urlPathResetPin;

		this.accountStateService.setPin(formValues.fieldFormControl);

		try {
			const formData: AccountModel = this.accountStateService.getFormValues();
			if (!this.isValidFormValues(formData)) {
				throw new Error(ErrorCodes.RegistrationFormIsNotValidError);
			}

			await this.userService.save(formData);

			await this.toastService.presentSuccess(
				this.translateService.instant(`${messageSource}.messages.success`),
				ToastDuration.Slow
			);

			await this.authProvider.setFusionUserId(formData.username);

			const workfiles: WorkspaceModel[] = await firstValueFrom(
				this.workspaceRepository.getAllByFusionUserId$(formData.username)
			);

			const workfilesFiltered = workfiles
				.filter((workFile: WorkspaceModel) => !workFile.pendingDelete)
				.sort((a, b) => a.importedUnix - b.importedUnix);

			if (workfilesFiltered.length === 0) {
				// no workfiles, redirect to import page
				await this.navController.navigateRoot(AppRoutingKeys.urlPathImport);
				return;
			}

			// redirect to last imported workfile
			await this.navController.navigateRoot(
				`/${AppRoutingKeys.urlPathWorkfile}/${workfilesFiltered.pop().workFileId}`
			);
		} catch (error) {
			await this.toastService.presentError(
				this.translateService.instant(`${messageSource}.messages.error`),
				ToastDuration.Slow
			);

			this.navController.navigateRoot(AppRoutingKeys.urlPathRegistration);
		}
	}

	private isValidFormValues(userData: AccountModel): boolean {
		return Object.values(userData).every((fieldValue: string) => {
			if (fieldValue) {
				return true;
			}

			return false;
		});
	}
}
